import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ReactFlow, Background, Controls, ConnectionMode, useNodesState } from "@xyflow/react";
import { Palette, Search, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PersonNode, RelEdge } from "@/components/relationships/GraphParts";
import { layoutNodes, typeMap, pairKey } from "@/components/relationships/layout";
import { RelDialog } from "@/components/relationships/RelDialog";
import { TypesDialog } from "@/components/relationships/TypesDialog";
import { NodePanel, EdgePanel } from "@/components/relationships/Panels";

const nodeTypes = { person: PersonNode };
const edgeTypes = { rel: RelEdge };

function Filters({ types, hidden, setHidden, showSugg, setShowSugg, q, setQ, canEdit, onTypes, counts, hiddenCount, onHidden }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 pb-1 [&>*]:shrink-0" data-testid="rel-filters">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-[#A8A090]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person…" className="h-9 w-48 pl-8" data-testid="rel-search-input" />
      </div>
      {types.map((t) => (
        <button key={t.key} type="button" onClick={() => setHidden(hidden.includes(t.key) ? hidden.filter((x) => x !== t.key) : [...hidden, t.key])}
          data-testid={`rel-filter-${t.key}`} className="rounded-full border px-2.5 py-1 text-xs transition-opacity"
          style={{ borderColor: `${t.color}88`, color: t.color, opacity: hidden.includes(t.key) ? 0.35 : 1 }}>
          {t.label}{counts[t.key] ? ` · ${counts[t.key]}` : ""}
        </button>
      ))}
      <label className="ml-2 flex items-center gap-2 whitespace-nowrap text-sm text-[#A8A090]"><Switch checked={showSugg} onCheckedChange={setShowSugg} data-testid="rel-show-suggested" />Suggestions</label>
      {hiddenCount > 0 && <Button size="sm" variant="ghost" onClick={onHidden} data-testid="rel-hidden-button"><EyeOff className="mr-1.5 h-4 w-4" />Hidden ({hiddenCount})</Button>}
      {canEdit && <Button size="sm" variant="ghost" onClick={onTypes} data-testid="rel-types-button"><Palette className="mr-1.5 h-4 w-4" />Types</Button>}
    </div>
  );
}

export default function RelationshipsPage() {
  const { universe, canEdit, base } = useOutletContext();
  const uid = universe.id;
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { data } = useQuery({ queryKey: ["rel-web", uid], queryFn: () => api.get(`/universes/${uid}/relationship-web`).then((r) => r.data) });
  const [hidden, setHidden] = useState([]);
  const [showSugg, setShowSugg] = useState(canEdit);
  const [rf, setRf] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [view, setView] = useState("web");
  const [hideNode, setHideNode] = useState(null);
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const refresh = () => invalidate(["rel-web", uid], ["entities", uid], "entity");

  const types = useMemo(() => data?.types || [], [data]);
  const tmap = useMemo(() => typeMap(types), [types]);
  const names = useMemo(() => Object.fromEntries((data?.nodes || []).map((n) => [n.id, n.name])), [data]);
  const links = useMemo(() => {
    const all = [...(data?.edges || []), ...(showSugg ? (data?.suggested || []).map((s) => ({ ...s, suggested: true })) : [])];
    return all.filter((e) => !hidden.includes(e.type));
  }, [data, showSugg, hidden]);
  const selItem = useMemo(() => {
    if (!sel || !data) return null;
    if (sel.kind === "node") return data.nodes.find((n) => n.id === sel.id) || null;
    return data.edges.find((e) => e.id === sel.id) || data.suggested.map((x) => ({ ...x, suggested: true })).find((x) => x.key === sel.id) || null;
  }, [sel, data]);
  const focusId = sel?.kind === "node" ? sel.id : null;
  const qHit = q.trim() ? (data?.nodes || []).find((n) => n.name.toLowerCase().includes(q.trim().toLowerCase()))?.id : null;
  const hub = focusId || qHit;
  const near = useMemo(() => (hub ? new Set([hub, ...links.filter((l) => l.a === hub || l.b === hub).flatMap((l) => [l.a, l.b])]) : null), [hub, links]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  useEffect(() => {
    if (!data) return;
    const pos = layoutNodes(data.nodes, data.edges);
    setNodes((prev) => {
      const had = Object.fromEntries(prev.map((n) => [n.id, n.position]));
      return data.nodes.map((n) => ({ id: n.id, type: "person", position: had[n.id] || pos[n.id], data: { ...n, canEdit } }));
    });
  }, [data, canEdit, setNodes]);
  const shownNodes = useMemo(() => nodes.map((n) => ({ ...n, data: { ...n.data, dim: near && !near.has(n.id), selected: n.id === hub } })), [nodes, near, hub]);

  const pickEdge = useCallback((e) => setSel({ kind: "edge", id: e.id || e.key }), []);
  useEffect(() => {
    const n = qHit && nodes.find((x) => x.id === qHit);
    if (rf && n) rf.setCenter(n.position.x + 56, n.position.y + 32, { zoom: 1.2, duration: 400 });
  }, [qHit, rf]); // eslint-disable-line react-hooks/exhaustive-deps
  const edges = useMemo(() => {
    const seen = {};
    return links.map((l) => {
      const k = pairKey(l.a, l.b);
      const idx = (seen[k] = (seen[k] ?? -1) + 1);
      const t = tmap[l.type] || { color: "#A8A090", label: l.type };
      return { id: l.id || `s-${l.key}`, source: l.a, target: l.b, type: "rel", selected: sel?.id === (l.id || l.key),
        data: { color: t.color, label: l.suggested ? `${l.label}?` : t.label, status: l.status, suggested: !!l.suggested, offset: idx ? (idx % 2 ? 1 : -1) * Math.ceil(idx / 2) : 0,
          dim: near && !(near.has(l.a) && near.has(l.b) && (l.a === hub || l.b === hub)), onClick: () => pickEdge(l) } };
    });
  }, [links, tmap, near, hub, sel, pickEdge]);
  const tree = useMemo(() => {
    if (!data) return { nodes: [], edges: [] };
    const pos = Object.assign({}, ...data.trees.map((t) => t.pos));
    const byId = Object.fromEntries(data.nodes.map((n) => [n.id, n]));
    const tn = Object.entries(pos).map(([id, [x, y]]) => ({ id, type: "person", position: { x, y }, draggable: false,
      data: { ...byId[id], canEdit, dim: near && !near.has(id), selected: id === hub } }));
    const te = edges.filter((e) => !e.data.suggested && pos[e.source] && pos[e.target] && ["family", "marriage", "love"].includes(links.find((l) => (l.id || `s-${l.key}`) === e.id)?.type));
    return { nodes: tn, edges: te };
  }, [data, edges, links, near, hub, canEdit]);
  const counts = useMemo(() => (data?.edges || []).reduce((m, e) => ({ ...m, [e.type]: (m[e.type] || 0) + 1 }), {}), [data]);

  const savePos = async (_, node) => {
    if (!canEdit) return;
    const positions = Object.fromEntries(nodes.map((n) => [n.id, n.id === node.id ? [node.position.x, node.position.y] : [n.position.x, n.position.y]]));
    try { await api.put(`/universes/${uid}/relationship-layout`, { positions }); } catch { /* keep local position */ }
  };
  const dismiss = async (s) => {
    try { await api.post(`/universes/${uid}/relationship-suggestions/dismiss`, { key: s.key }); setSel(null); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/entities/${del.id}`); setSel(null); refresh(); toast.success("Relationship deleted"); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  const openEntry = (id) => navigate(`${base}/lore/${id}`);
  const setHidden_ = async (id, hidden) => {
    try {
      await api.post(`/universes/${uid}/relationship-hidden`, { entity_id: id, hidden });
      setSel(null);
      refresh();
      toast.success(hidden ? "Removed from the Relationship Web" : "Restored to the Relationship Web");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-4 py-8 md:px-10 lg:px-14" data-testid="relationships-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="lf-label mb-2">{data?.edges.length ?? 0} relationship{data?.edges.length === 1 ? "" : "s"} · {data?.nodes.length ?? 0} {data?.nodes.length === 1 ? "person or faction" : "people & factions"}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">Relationship Web</h1>
          {canEdit && <p className="mt-2 text-sm text-[#A8A090]">Drag the amber dot on a person onto someone else to link them. Click a line to edit it.</p>}
        </div>
      </div>
      <div className="mb-3 flex rounded-md border border-[#2D3442] bg-[#13171F] p-1 w-fit" data-testid="rel-view-switch">
        {[["web", "Web"], ["tree", "Family tree"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setView(v)} data-testid={`rel-view-${v}`}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${view === v ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>{l}</button>
        ))}
      </div>
      <Filters types={types} hidden={hidden} setHidden={setHidden} showSugg={showSugg} setShowSugg={setShowSugg} q={q} setQ={setQ} canEdit={canEdit} onTypes={() => setTypesOpen(true)} counts={counts} hiddenCount={data?.hidden?.length || 0} onHidden={() => setHiddenOpen(true)} />
      <div className="relative h-[calc(100vh-260px)] min-h-[520px] overflow-hidden rounded-md border border-[#2D3442] bg-[#0F131A]" data-testid="rel-canvas">
        {data && data.nodes.length === 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-8 text-center text-[#A8A090]" data-testid="rel-empty">Add some Characters or Factions to the Lore Bible to start your web.</div>
        )}
        {view === "tree" && data && data.trees.length === 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-8 text-center text-[#A8A090]" data-testid="rel-tree-empty">No family links yet. Link two people as Family and choose who is the parent to grow a tree.</div>
        )}
        <ReactFlow key={view} nodes={view === "tree" ? tree.nodes : shownNodes} edges={view === "tree" ? tree.edges : edges} nodesDraggable={view === "web"} nodeTypes={nodeTypes} edgeTypes={edgeTypes} onNodesChange={onNodesChange} onNodeDragStop={savePos}
          connectionMode={ConnectionMode.Loose} connectionRadius={55} nodesConnectable={canEdit} onConnect={(c) => c.source !== c.target && setCreating({ pair: [c.source, c.target] })}
          onInit={setRf} onNodeClick={(_, n) => setSel({ kind: "node", id: n.id })} onEdgeClick={(_, e) => { const l = links.find((x) => (x.id || `s-${x.key}`) === e.id); if (l) pickEdge(l); }}
          onPaneClick={() => setSel(null)} fitView fitViewOptions={{ padding: 0.25 }} minZoom={0.2} proOptions={{ hideAttribution: true }} colorMode="dark">
          <Background color="#2D3442" gap={28} />
          <Controls showInteractive={false} />
        </ReactFlow>
        {sel?.kind === "node" && selItem && <NodePanel node={selItem} edges={links} names={names} tmap={tmap} canEdit={canEdit} onRemove={setHideNode} onClose={() => setSel(null)} onOpenEntry={openEntry} onPickEdge={pickEdge} />}
        {sel?.kind === "edge" && selItem && (
          <EdgePanel key={sel.id} edge={selItem} names={names} types={types} tmap={tmap} canEdit={canEdit} onClose={() => setSel(null)} onOpenEntry={openEntry}
            onChanged={() => { refresh(); toast.success("Relationship updated"); }} onConfirm={(s) => setCreating({ pair: [s.a, s.b], type: s.type })} onDismiss={dismiss} onDelete={setDel} />
        )}
      </div>
      {creating && <RelDialog uid={uid} pair={creating.pair} names={names} types={types} initialType={creating.type} onClose={() => setCreating(null)} onSaved={() => { setSel(null); refresh(); }} />}
      {typesOpen && <TypesDialog uid={uid} types={types} onClose={() => setTypesOpen(false)} onSaved={refresh} />}
      <ConfirmDialog open={!!hideNode} onOpenChange={(o) => !o && setHideNode(null)} onConfirm={() => { setHidden_(hideNode.id, true); setHideNode(null); }}
        title={`Remove ${hideNode?.name} from the Relationship Web?`} confirmLabel="Remove from web"
        description="This does not delete the entry. It only hides them (and their lines) from the Relationship Web, family trees and the PDF's relationship pages. Their entry and Relationship entries stay in the Lore Bible, and you can bring them back any time from “Hidden”." />
      {hiddenOpen && (
        <Dialog open onOpenChange={setHiddenOpen}>
          <DialogContent className="max-w-md" data-testid="rel-hidden-dialog">
            <DialogHeader><DialogTitle className="font-display text-3xl">Hidden from the web</DialogTitle></DialogHeader>
            <div className="space-y-1.5 py-2">
              {(data?.hidden || []).length === 0 && <p className="text-sm text-[#A8A090]">Nobody is hidden.</p>}
              {(data?.hidden || []).map((h) => (
                <div key={h.id} className="flex items-center justify-between rounded-md border border-[#2D3442] px-3 py-2" data-testid={`rel-hidden-${h.id}`}>
                  <span className="text-sm text-[#F3EBDD]">{h.name} <span className="text-xs text-[#A8A090]">· {h.category}</span></span>
                  {canEdit && <Button size="sm" variant="outline" onClick={() => setHidden_(h.id, false)} data-testid={`rel-restore-${h.id}`}><Eye className="mr-1.5 h-3.5 w-3.5" />Restore</Button>}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} title={`Delete ${del?.name}?`} description="The Relationship entry will be removed from the Lore Bible." />
    </div>
  );
}
